import { z } from 'zod';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';

import { Card } from '@repo/ui/Card';
import { Button } from '@repo/ui/Button';
import { Form, TextInputField, TextAreaField } from '@repo/ui/Form';
import { useZodForm } from '@repo/hooks/useZodForm';

import { sharedSwitchConfirmation, type AffectedNumbers } from '../lib/sharedSwitchConfirmation';

const UPDATE_SWITCH_DETAILS = gql`
  mutation UpdateSwitchDetails($id: ID!, $input: UpdateSwitchInput!) {
    updateSwitch(id: $id, input: $input) {
      switch {
        id
      }
    }
  }
`;

const schema = z.object({
  name: z.string().min(2, { message: 'Switch name must contain at least 2 characters.' }),
  notes: z.string().optional(),
});

interface UpdateSwitchFormProps {
  switchId: string;
  name: string;
  notes: string | null;
  canUpdate: boolean;
  refetch: () => void;
  /** Other numbers share this switch: confirm before saving */
  affectedNumbers?: AffectedNumbers;
}

const UpdateSwitchForm = ({
  switchId,
  name,
  notes,
  canUpdate,
  refetch,
  affectedNumbers,
}: UpdateSwitchFormProps) => {
  const client = useApolloClient();

  const form = useZodForm({
    schema,
    defaultValues: { name, notes: notes ?? '' },
    confirm: () => sharedSwitchConfirmation(affectedNumbers, 'details'),
    onSubmit: async (input) => {
      await client.mutate({ mutation: UPDATE_SWITCH_DETAILS, variables: { id: switchId, input } });
      refetch();
    },
  });

  return (
    <Card title="Details">
      <Form {...form} column={1}>
        <Form.Success>Switch was successfully updated.</Form.Success>
        <Form.Error>Unable to update switch, please try again.</Form.Error>
        <TextInputField id="name" label="Name" disabled={!canUpdate} {...form.register('name')} />
        <TextAreaField id="notes" label="Notes" disabled={!canUpdate} {...form.register('notes')} />
        {canUpdate && (
          <div className="sm:col-span-2">
            <Button type="submit">Save changes</Button>
          </div>
        )}
      </Form>
    </Card>
  );
};

export default UpdateSwitchForm;
